import { AnimatePresence, motion } from 'framer-motion';
import { openAccountSettings } from '~/lib/stores/accountSettings';
import { usePlanAccess } from '~/lib/planGate';
import type { LlmErrorAlertType } from '~/types/actions';
import { classNames } from '~/utils/classNames';

interface Props {
  alert: LlmErrorAlertType;
  clearAlert: () => void;
  onRetry?: () => void;
  /** Fired by the Continue button on a mid-build credit cutoff — resumes the
   *  paused build from where it stopped. */
  onContinue?: () => void;
}

export default function LlmErrorAlert({ alert, clearAlert, onRetry, onContinue }: Props) {
  const { title, description, provider, errorType, midBuild, tier } = alert;
  const isCredits = errorType === 'credits_exhausted';
  // Plus/Pro sit beside the website tier (which the server reports here), so the
  // account's plan decides Upgrade vs Top up once it's known
  const access = usePlanAccess();
  const isFree = access.planUnknown || access.tier === undefined ? (tier ?? 'free') === 'free' : access.isFree;
  // A credit cutoff, a transient capacity shed or a dropped connection is a
  // recoverable pause → warning (neutral); a real provider error is danger
  // (red). Drives the icon + card tint. Full class strings (not interpolated)
  // so UnoCSS's JIT can see them.
  const isWarning = isCredits || errorType === 'capacity' || errorType === 'network';

  const getErrorIcon = () => {
    switch (errorType) {
      case 'authentication':
        return 'i-ph:key-duotone';
      case 'rate_limit':
      case 'capacity':
        return 'i-ph:clock-duotone';
      case 'quota':
      case 'credits_exhausted':
        return 'i-ph:coins-duotone';
      case 'network':
        return 'i-ph:wifi-slash-duotone';
      default:
        return 'i-ph:warning-duotone';
    }
  };

  const getErrorMessage = () => {
    switch (errorType) {
      case 'authentication':
        return 'This provider is not available. Try a different provider or model.';
      case 'rate_limit':
        return `Rate limit exceeded for ${provider}. Please wait before retrying.`;
      case 'capacity':
        return 'The AI service is at full capacity right now. Nothing was started or billed — retry in a moment.';
      case 'network':
        return "We couldn't reach Dunea — check your internet connection. Your work is saved, and any build in progress keeps running on our side. Hit Retry once you're back online.";
      case 'server':
        return 'Something went wrong on our end — this one is on us, not you. Your work is saved. Give it a moment and hit Retry.';
      case 'quota':
        return `Quota exceeded for ${provider}. Please check your account limits.`;
      case 'credits_exhausted':
        return midBuild
          ? `You ran out of credits mid-build — your progress is saved. ${
              isFree ? 'Upgrade' : 'Top up'
            } your credits, then hit Continue to finish where it left off.`
          : 'You have run out of credits. Reach us on Discord (https://discord.gg/mCdjW3Ztv8) or wait for your next credit reset.';
      default:
        return 'An error occurred while processing your request.';
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className={classNames(
          'font-geist rounded-xl border p-4 mb-2',
          // Credit pause = neutral dark card (on-theme); the lime Upgrade button
          // is the only accent. A real provider error keeps a subtle danger tint.
          isWarning ? 'border-subtle bg-raised' : 'border-danger/25 bg-danger/[0.05]',
        )}
      >
        <div className="flex items-start gap-3">
          <motion.div
            className="flex-shrink-0"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className={classNames(getErrorIcon(), 'text-xl', isWarning ? 'text-warning' : 'text-danger')}></div>
          </motion.div>

          <div className="flex-1">
            <motion.h3
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-[15px] font-semibold tracking-[-0.01em] text-primary"
            >
              {title}
            </motion.h3>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mt-1.5 text-[13px] leading-relaxed text-secondary [overflow-wrap:anywhere]"
            >
              <p>{getErrorMessage()}</p>

              {description && (
                <div className="ui-mono mt-3 rounded-lg border border-hairline bg-sunken p-2.5 text-[11px] text-muted [overflow-wrap:anywhere]">
                  {description}
                </div>
              )}
            </motion.div>

            <motion.div
              className="mt-3.5"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex flex-wrap items-center gap-2">
                {isCredits ? (
                  <button
                    onClick={() => openAccountSettings(isFree ? 'plans' : 'topup', 'credits_alert')}
                    className="ui-btn-accent"
                  >
                    {isFree ? 'Upgrade' : 'Top up credits'}
                  </button>
                ) : (
                  onRetry && (
                    <button onClick={onRetry} className="ui-btn-accent">
                      Retry
                    </button>
                  )
                )}
                {isCredits && midBuild && onContinue && (
                  <button
                    onClick={onContinue}
                    className="ui-btn-base rounded-full border border-subtle bg-white/[0.06] px-[18px] py-[9px] text-[13px] text-primary hover:border-prominent hover:bg-white/[0.10]"
                  >
                    <div className="i-ph:play-bold text-[13px]"></div>
                    Continue build
                  </button>
                )}
                <button
                  onClick={clearAlert}
                  className="ui-btn-base rounded-full border border-hairline bg-white/[0.02] px-[18px] py-[9px] text-[13px] text-secondary hover:bg-white/[0.05] hover:text-primary"
                >
                  <div className="i-ph:x-bold text-[13px]"></div>
                  Dismiss
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
